import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from 'redux/store';
import BillingHistory, { BillingHistoryItem } from '../BillingHistory/BillingHistory';
import CurrentSubscription from '../CurrentSubscription/CurrentSubscription';
import UsageMetrics from '../UsageMetrics/UsageMetrics';
import UsageHistory from '../UsageHistory/UsageHistory';
import OutstandingInvoices, {
    OutstandingInvoice,
} from '../OutstandingInvoices/OutstandingInvoices';
import ChangePackageModal from '../Modals/ChangePackageModal';
import BillingSettingsModal from '../Modals/BillingSettingsModal';
import * as S from './MyAliroBillingView.styled';
import {
    fetchBillingData,
    updateBillingSettings,
    clearError,
    clearSuccessMessage,
    changePackage,
    cancelSubscription,
} from 'redux/myaliro-billing';
import * as billingApi from 'api/rest/myaliro_billing';

export default function MyAliroBillingView() {
    const dispatch = useDispatch<AppDispatch>();

    // Redux state
    const {
        subscription,
        outstandingInvoices,
        // invoices removed - not used
        // paymentMethods removed - managed in sessionStorage
        settings,
        currentUsage,
        usageHistory,
        isLoading,
        error,
        successMessage,
    } = useSelector((state: RootState) => state.myaliroBilling);

    // Local state for payment history
    const [paymentHistory, setPaymentHistory] = useState<billingApi.PaymentHistoryItem[]>([]);

    // Modal states
    const [changePackageModalOpen, setChangePackageModalOpen] = useState(false);
    const [settingsModalOpen, setSettingsModalOpen] = useState(false);

    // Load billing data on mount
    useEffect(() => {
        dispatch(fetchBillingData());
        loadPaymentHistory();
    }, [dispatch]);

    // Load payment history (all payment attempts including failed)
    const loadPaymentHistory = async () => {
        try {
            const response = await billingApi.getPaymentHistory();
            setPaymentHistory(response.data.payment_history || []);
        } catch (err) {
            console.error('Failed to load payment history:', err);
            // Don't show error to user, just log it
        }
    };

    // Show success/error messages
    useEffect(() => {
        if (error) {
            alert(`Error: ${error}`);
            dispatch(clearError());
        }
        if (successMessage) {
            alert(successMessage);
            dispatch(clearSuccessMessage());
            // Refresh data after successful operation
            dispatch(fetchBillingData());
        }
    }, [error, successMessage, dispatch]);

    // Payment history from Stripe API already matches BillingHistoryItem interface
    // No transformation needed - direct type cast
    const transformedBillingHistory: BillingHistoryItem[] = paymentHistory as BillingHistoryItem[];

    // Handlers
    const handleViewInvoice = (invoice: OutstandingInvoice) => {
        if (invoice.hosted_invoice_url) {
            window.open(invoice.hosted_invoice_url, '_blank');
        } else {
            alert('Invoice URL not available');
        }
    };

    const handlePayNow = (invoice: OutstandingInvoice) => {
        // Redirect to Stripe Customer Portal for payment
        handleManageBilling();
    };

    const handleDownloadReceipt = async (url: string) => {
        // Legacy function - open URL directly
        // Invoice PDFs are now hosted by Stripe
        if (url) {
            window.open(url, '_blank');
        }
    };

    const handleChangePackage = () => {
        setChangePackageModalOpen(true);
    };

    const handleGetStarted = () => {
        setChangePackageModalOpen(true);
    };

    const handleConfirmPackageChange = async (
        newPackageType: string,
        newBillingPeriod: 'MONTHLY' | 'ANNUAL'
    ) => {
        try {
            if (!subscription) {
                // CREATION MODE - Stripe Checkout handles payment
                // Modal will redirect to Stripe Checkout
                setChangePackageModalOpen(false);
            } else {
                // CHANGE MODE - Change existing subscription package
                await dispatch(
                    changePackage({
                        new_package_type: newPackageType,
                        new_billing_period: newBillingPeriod,
                    })
                ).unwrap();

                setChangePackageModalOpen(false);
                alert('Package change requested successfully');
            }
        } catch (err) {
            console.error('Error with subscription:', err);
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            alert(
                `Failed to ${subscription ? 'change package' : 'create subscription'}: ${errorMessage}`
            );
        }
    };

    const handleCancelSubscription = async () => {
        if (
            window.confirm(
                'Are you sure you want to cancel your subscription? This will take effect at the end of the current billing period.'
            )
        ) {
            try {
                await dispatch(cancelSubscription()).unwrap();
                alert('Subscription cancelled successfully');
            } catch (err) {
                console.error('Error cancelling subscription:', err);
                const errorMessage = err instanceof Error ? err.message : 'Unknown error';
                alert(`Failed to cancel subscription: ${errorMessage}`);
            }
        }
    };

    const handleManageBilling = async () => {
        try {
            const returnUrl = `${window.location.origin}/account/financials?tab=my-billing`;
            const response = await billingApi.createCustomerPortalSession(returnUrl);
            // Redirect to Stripe Customer Portal
            window.location.href = response.data.url;
        } catch (err) {
            console.error('Failed to open billing portal:', err);
            const errorMessage = err instanceof Error ? err.message : 'Unknown error';
            alert(`Failed to open billing portal: ${errorMessage}`);
        }
    };

    const handleSaveSettingsFromModal = (newSettings: {
        auto_billing_enabled: boolean;
        billing_email: string;
    }) => {
        dispatch(updateBillingSettings(newSettings));
    };

    if (isLoading) {
        return (
            <S.Container>
                <S.Description>Loading billing information...</S.Description>
            </S.Container>
        );
    }

    return (
        <S.Container>
            <S.HeaderSection>
                <div>
                    <S.Title>My Billing</S.Title>
                    <S.Description>
                        Manage your billing with Aliro, payment methods, and view your payment
                        history
                    </S.Description>
                </div>
                {subscription && (
                    <S.ButtonGroup>
                        <S.SettingsButton onClick={() => setSettingsModalOpen(true)}>
                            ⚙️ Settings
                        </S.SettingsButton>
                        <S.ManageBillingButton onClick={handleManageBilling}>
                            Manage Billing
                        </S.ManageBillingButton>
                    </S.ButtonGroup>
                )}
            </S.HeaderSection>

            <CurrentSubscription
                subscription={subscription}
                onChangePackage={handleChangePackage}
                onCancelSubscription={handleCancelSubscription}
                onGetStarted={handleGetStarted}
            />

            <UsageMetrics usage={currentUsage} subscription={subscription} />

            <UsageHistory usageHistory={usageHistory} />

            {outstandingInvoices && outstandingInvoices.length > 0 && (
                <OutstandingInvoices
                    invoices={outstandingInvoices}
                    onViewInvoice={handleViewInvoice}
                    onPayNow={handlePayNow}
                />
            )}

            <BillingHistory
                history={transformedBillingHistory}
                onDownloadReceipt={handleDownloadReceipt}
            />

            {/* Modals */}
            <ChangePackageModal
                open={changePackageModalOpen}
                currentSubscription={subscription}
                hasPaymentMethods={true}
                onClose={() => setChangePackageModalOpen(false)}
                onConfirm={handleConfirmPackageChange}
            />

            <BillingSettingsModal
                open={settingsModalOpen}
                settings={settings}
                onClose={() => setSettingsModalOpen(false)}
                onSave={handleSaveSettingsFromModal}
            />
        </S.Container>
    );
}
